import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, defer, finalize, forkJoin, map, of, timeout } from 'rxjs';

import { ModulesService } from '../../api/services';
import { ModulesStore } from '../../features/admin/modules/modules.store';
import type { FilterItem } from '../../features/admin/modules/modules.model';
import { debugLog } from '../utils/debug-logger';
import { type ExternalEntriesCount, externalEntriesKey, isExternalEntriesSatisfied } from './filter-evaluator.util';

export { externalEntriesKey };

/** Archived entries never count towards an "does this exist" question. */
const ARCHIVED_FILTER = 'entry.archived eq false';
const EXTERNAL_ENTRIES_TIMEOUT_MS = 15_000;

/** One resolved "how many entries match this" question. */
export interface ExternalEntriesQuery {
  /** `externalEntriesKey(moduleCode, filter)` — the whole identity of the question. */
  key: string;
  moduleCode: string;
  /** OData filter, already trimmed. Empty = every (non-archived) entry of the module. */
  filter: string;
  /**
   * Highest `minCount` asked of this key. `$top` is bounded to it, so one response answers every
   * condition sharing the key without ever fetching more rows than the answer needs.
   */
  top: number;
}

/** Result of one resolve attempt. Cached counts may be older; `answeredKeys` belong to this attempt. */
export interface ExternalEntriesResolution {
  counts: Record<string, ExternalEntriesCount>;
  answeredKeys: readonly string[];
}

/** Filter items that ask an external-entries question, deduplicated into one query per key. */
export function collectExternalEntriesQueries(filters: readonly FilterItem[]): ExternalEntriesQuery[] {
  const queries = new Map<string, ExternalEntriesQuery>();

  for (const filter of filters) {
    if (filter?.basedOn !== 'externalEntries' || !filter.moduleCode) {
      continue;
    }
    const odata = filter.filter?.trim() ?? '';
    const key = externalEntriesKey(filter.moduleCode, odata);
    // A minCount of 0 would make the condition true without any data, which is never what the
    // author meant; 1 is the documented default.
    const minCount = Math.max(1, filter.minCount ?? 1);
    const existing = queries.get(key);
    if (existing) {
      existing.top = Math.max(existing.top, minCount);
      continue;
    }
    queries.set(key, { key, moduleCode: filter.moduleCode, filter: odata, top: minCount });
  }

  return [...queries.values()];
}

/** Prefix every question with the archived guard, parenthesising an `or` so it cannot leak out. */
export function buildExternalEntriesFilter(filter: string): string {
  if (!filter) {
    return ARCHIVED_FILTER;
  }
  const clause = /\bor\b/i.test(filter) ? `(${filter})` : filter;
  return `${ARCHIVED_FILTER} and ${clause}`;
}

/**
 * Resolves `basedOn: 'externalEntries'` conditions — "do at least N entries exist in module X
 * matching filter Y".
 *
 * The backend evaluates this shape for action conditions; there was no front-end equivalent, so
 * every interface surface that wanted the question had to do without. This service is that
 * equivalent.
 *
 * WHY A CACHE RATHER THAN AN ASYNC EVALUATOR: the question needs a request, but
 * `evaluateFiltersAgainstEntry` is synchronous and called from templates, computed signals and
 * planning render paths where an Observable is not an option. So the answer is resolved ahead of
 * time into `counts()` and the sync evaluator only ever reads from it — the same split
 * `MappingExpressionService` uses for JSONata mappings, which resolves async and hands plain values
 * to the sync `fillEntryWithMappingItems`.
 *
 * An unresolved key reads as "not satisfied" rather than throwing: a condition that has not been
 * answered yet must not flash its true-state before the count lands.
 */
@Injectable({ providedIn: 'root' })
export class ExternalEntriesEvaluatorService {
  readonly #modulesService = inject(ModulesService);
  readonly #modulesStore = inject(ModulesStore);

  readonly #counts = signal<Record<string, ExternalEntriesCount>>({});

  /** Largest requested `top` that has come back per key, answered OR failed. See {@link hasSettled}. */
  readonly #settledTopByKey = signal<ReadonlyMap<string, number>>(new Map<string, number>());

  /** Resolved counts by `externalEntriesKey`. Read this straight into the sync evaluator. */
  readonly counts = computed(() => this.#counts());

  /** One pass at a time; a call arriving mid-pass is collapsed into a single follow-up pass. */
  #passInFlight = false;
  #passQueued: ExternalEntriesQuery[] | null = null;

  /**
   * Resolve the given queries into `counts()`. Safe to call as often as you like — a burst of
   * triggers can never fan out into overlapping request storms.
   *
   * Emits once the counts are in (or immediately when there is nothing to ask), so a caller that
   * needs to re-render afterwards can subscribe rather than poll.
   */
  resolve(queries: readonly ExternalEntriesQuery[]): Observable<ExternalEntriesResolution> {
    // defer, so the in-flight flag is claimed on SUBSCRIBE rather than on call. Claiming it eagerly
    // would wedge the service for good the moment a caller built the observable without
    // subscribing — the flag would never be cleared, because #finishPass runs inside the pipe.
    return defer(() => {
      if (!queries.length) {
        return of({ counts: this.#counts(), answeredKeys: [] });
      }

      if (this.#passInFlight) {
        // Merge rather than replace. Dropping the older set would only be safe while a single
        // caller passes everything it cares about every time; with two callers the loser's
        // questions would silently never be asked.
        this.#passQueued = mergeQueries(this.#passQueued, queries);
        return of({ counts: this.#counts(), answeredKeys: [] });
      }

      this.#passInFlight = true;
      debugLog('ExternalEntries', 'resolve pass', { queries: queries.length });

      return this.#runPass(queries);
    });
  }

  #runPass(queries: readonly ExternalEntriesQuery[]): Observable<ExternalEntriesResolution> {
    return forkJoin(queries.map(query => this.#count(query))).pipe(
      map(results => {
        this.#markSettled(queries);
        const resolved = results.filter(result => result.count !== null);
        this.#counts.update(current => {
          const next = { ...current };
          let changed = false;
          for (const result of results) {
            if (result.count === null) {
              if (result.key in next) {
                delete next[result.key];
                changed = true;
              }
              continue;
            }
            const existing = next[result.key];
            const incoming = { count: result.count, top: result.top };
            // A wider pass supersedes a narrower one. A narrow result may also replace it when
            // it is definitive: fewer rows than `$top` proves the exact total. A full narrow page
            // remains capped and cannot safely overwrite a wider answer.
            if (!existing || incoming.top >= existing.top || incoming.count < incoming.top) {
              next[result.key] = incoming;
              changed = true;
            }
          }
          return changed ? next : current;
        });
        return { counts: this.#counts(), answeredKeys: resolved.map(result => result.key) };
      }),
      catchError((error: unknown) => {
        // Every request already maps its own failure to null, so this only fires on a programming
        // error. Clear stale answers and settle the pass so UI can recover honestly.
        debugLog('ExternalEntries', 'resolve pass failed', error);
        this.#counts.update(current => {
          const next = { ...current };
          let changed = false;
          for (const query of queries) {
            if (query.key in next) {
              delete next[query.key];
              changed = true;
            }
          }
          return changed ? next : current;
        });
        this.#markSettled(queries);
        return of({ counts: this.#counts(), answeredKeys: [] });
      }),
      finalize(() => this.#finishPass())
    );
  }

  /**
   * Is the condition satisfied right now? `undefined` count = not resolved yet, which reads as
   * false so a condition never flashes true before its answer lands.
   */
  isSatisfied(moduleCode: string | undefined, filter: string | undefined, minCount?: number): boolean {
    if (!moduleCode) {
      return false;
    }
    return isExternalEntriesSatisfied(this.#counts()[externalEntriesKey(moduleCode, filter?.trim() ?? '')], minCount);
  }

  /**
   * Has this question been asked and come back yet?
   *
   * Deliberately "asked and came back", not "answered": a caller that hides UI until its questions
   * are settled must not be held hostage by a count that keeps failing. A failed key settles with
   * no recorded count, so the caller resumes on the unresolved-reads-as-false rule above.
   *
   * The point of the distinction is the pre-answer window: `isSatisfied` cannot tell "no matching
   * entries" from "not asked yet", so a surface whose visibility depends on the answer would paint
   * its unresolved state for a frame and then correct itself.
   */
  hasSettled(key: string, top = 1): boolean {
    if ((this.#settledTopByKey().get(key) ?? 0) >= top) {
      return true;
    }
    const count = this.#counts()[key];
    return count !== undefined && count.count < count.top;
  }

  /** One pass came back; every key is settled up to the precision it asked for, answered or not. */
  #markSettled(queries: readonly ExternalEntriesQuery[]): void {
    this.#settledTopByKey.update(current => {
      if (queries.every(query => (current.get(query.key) ?? 0) >= query.top)) {
        return current;
      }
      const next = new Map(current);
      for (const query of queries) {
        next.set(query.key, Math.max(next.get(query.key) ?? 0, query.top));
      }
      return next;
    });
  }

  #finishPass(): void {
    this.#passInFlight = false;
    const queued = this.#passQueued;
    if (queued) {
      this.#passQueued = null;
      this.resolve(queued).subscribe();
    }
  }

  /**
   * Count through the generated `ModulesService.getModuleEntries` — the same endpoint every module
   * list and widget uses, with `$top` bounding the payload to what the answer needs. No new API
   * surface, and no detour through `EntriesService` so this cannot flip a module list's shared
   * loading flag.
   */
  #count(query: ExternalEntriesQuery): Observable<{ key: string; count: number | null; top: number }> {
    const moduleId = this.#modulesStore.moduleCodeToId(query.moduleCode);
    if (!moduleId) {
      debugLog('ExternalEntries', 'unknown moduleCode', query.moduleCode);
      return of({ key: query.key, count: null, top: query.top });
    }

    return this.#modulesService
      .getModuleEntries({
        moduleId,
        $filter: buildExternalEntriesFilter(query.filter),
        $skip: '0',
        $top: String(query.top),
      })
      .pipe(
        timeout({ first: EXTERNAL_ENTRIES_TIMEOUT_MS }),
        map(entries => ({ key: query.key, count: entries?.length ?? 0, top: query.top })),
        catchError((error: unknown) => {
          debugLog('ExternalEntries', 'count failed', { query, error });
          return of({ key: query.key, count: null, top: query.top });
        })
      );
  }
}

/** Union of two query sets by key, keeping the widest `$top` asked for each question. */
function mergeQueries(
  existing: ExternalEntriesQuery[] | null,
  incoming: readonly ExternalEntriesQuery[]
): ExternalEntriesQuery[] {
  const merged = new Map<string, ExternalEntriesQuery>();
  for (const query of [...(existing ?? []), ...incoming]) {
    const current = merged.get(query.key);
    merged.set(query.key, current ? { ...current, top: Math.max(current.top, query.top) } : { ...query });
  }
  return [...merged.values()];
}
